import {
  Apps,
  Apartment,
  Diversity3,
  Diversity2,
  CurrencyRupee,
  Receipt,
  AccountBalanceWallet,
  CalendarMonthRounded,
  PendingActions,
  Forum,
  QuestionAnswer,
  NoteAdd,
  Visibility,
  Dashboard,
  PostAdd,
  Settings,
  MenuBook,
  Store,
  PersonPin,
  LocalHospital,
  Campaign,
  HowToVote,
  FolderOpen,
  Groups,
  Build,
  Event,
  LocalShipping,
  Shield,
  DynamicFeed,
  Public,
  EmojiEvents,
  AutoStories,
  Redeem,
  Extension,
  SportsEsports,
  Psychology,
} from "@mui/icons-material";

/**
 * Centralized route configuration for the entire app.
 * Used by AppMenu, Breadcrumbs, AppBar title, and BottomNav.
 *
 * Menu sections (in display order):
 *   null          — top-level (Home)
 *   "My Community" — community setup, people, committee
 *   "Finance"     — accounts, projects, dues, vendors, payments
 *   "Communication" — chats, discussion, notices, polls, invitations
 *   "Services"    — amenities, maintenance, daily help, documents, emergency, move tracking
 *   "Social"      — social hub
 *   "Help"        — how to use
 */

const routeConfig = [
  // ── Top-level ──
  { path: "/", label: "Home", icon: Apps, showInMenu: true, menuSection: null },
  { path: "/calendar", label: "Calendar", icon: CalendarMonthRounded, showInMenu: true, menuSection: null },
  { path: "/wizards", label: "Insights", icon: Dashboard, showInMenu: true, menuSection: null },

  // ── My Community ──
  { path: "/community", label: "Community", icon: Apartment, showInMenu: true, menuSection: "My Community" },
  { path: "/association", label: "Committee", icon: Diversity3, showInMenu: true, menuSection: "My Community" },
  { path: "/people", label: "People", icon: Diversity2, showInMenu: true, menuSection: "My Community" },
  { path: "/people/cross-community", label: "Cross-Community", icon: Public, showInMenu: true, menuSection: "My Community", parent: "/people" },

  // ── Finance ──
  { path: "/works", label: "Development", icon: PendingActions, showInMenu: true, menuSection: "Finance" },
  { path: "/account", label: "Fund Accounts", icon: CurrencyRupee, showInMenu: true, menuSection: "Finance" },
  { path: "/account-form", label: "New Account Head", icon: PostAdd, showInMenu: false, menuSection: "Finance", parent: "/account" },
  { path: "/account-details", label: "Account Details", icon: Settings, showInMenu: false, menuSection: "Finance", parent: "/account" },
  { path: "/fund-raise-details", label: "Fund Raise Details", icon: Settings, showInMenu: false, menuSection: "Finance", parent: "/account" },
  { path: "/projects", label: "Development", icon: PendingActions, showInMenu: false, menuSection: "Finance", parent: "/works" },
  { path: "/project", label: "New Project", icon: NoteAdd, showInMenu: false, menuSection: "Finance", parent: "/works" },
  { path: "/project/:projectNumber", label: "Project Details", icon: Visibility, showInMenu: false, menuSection: "Finance", parent: "/works" },
  { path: "/dues", label: "Dues", icon: Receipt, showInMenu: true, menuSection: "Finance", parent: "/account" },
  { path: "/review-payments", label: "Review Payments", icon: AccountBalanceWallet, showInMenu: false, menuSection: "Finance", parent: "/billing-inputs" },
  { path: "/vendors", label: "Vendors", icon: Store, showInMenu: true, menuSection: "Finance" },
  { path: "/billing-inputs", label: "Bill Management", icon: PendingActions, showInMenu: true, menuSection: "Finance" },
  { path: "/board", label: "Board", icon: Dashboard, showInMenu: false, menuSection: "Finance", parent: "/works" },

  // ── Communication ──
  { path: "/chats", label: "Chats", icon: Forum, showInMenu: true, menuSection: "Communication" },
  { path: "/discussion", label: "Discussion", icon: QuestionAnswer, showInMenu: false, menuSection: "Communication" },
  { path: "/notices", label: "Notices", icon: Campaign, showInMenu: true, menuSection: "Communication" },
  { path: "/polls", label: "Polls", icon: HowToVote, showInMenu: true, menuSection: "Communication" },
  { path: "/invitations", label: "Meetings", icon: CalendarMonthRounded, showInMenu: true, menuSection: "Communication" },
  { path: "/invite", label: "New Invitation", icon: NoteAdd, showInMenu: false, menuSection: "Communication", parent: "/invitations" },

  // ── Services ──
  { path: "/visitors", label: "Visitors", icon: PersonPin, showInMenu: true, menuSection: "Services" },
  { path: "/amenities", label: "Amenities", icon: Event, showInMenu: true, menuSection: "Services" },
  { path: "/maintenance", label: "Maintenance", icon: Build, showInMenu: true, menuSection: "Services" },
  { path: "/daily-help", label: "Daily Help", icon: Groups, showInMenu: true, menuSection: "Services" },
  { path: "/documents", label: "Documents", icon: FolderOpen, showInMenu: true, menuSection: "Services" },
  { path: "/emergency", label: "Emergency", icon: LocalHospital, showInMenu: true, menuSection: "Services" },
  { path: "/move-tracking", label: "Move In/Out", icon: LocalShipping, showInMenu: true, menuSection: "Services" },

  // ── Social ──
  { path: "/social", label: "Social Hub", icon: DynamicFeed, showInMenu: true, menuSection: "Social" },

  // ── Engagement ──
  { path: "/engagement", label: "Engagement", icon: EmojiEvents, showInMenu: true, menuSection: "Engagement" },
  { path: "/engagement/stories", label: "Story Wall", icon: AutoStories, showInMenu: true, menuSection: "Engagement" },
  { path: "/engagement/offerings", label: "Offerings", icon: Redeem, showInMenu: true, menuSection: "Engagement" },
  { path: "/engagement/buddy-groups", label: "Buddy Groups", icon: Groups, showInMenu: true, menuSection: "Engagement" },
  { path: "/engagement/puzzles", label: "Daily Puzzles", icon: Extension, showInMenu: true, menuSection: "Engagement" },
  { path: "/engagement/games", label: "Games", icon: SportsEsports, showInMenu: true, menuSection: "Engagement" },
  { path: "/engagement/contribute", label: "Contribute", icon: NoteAdd, showInMenu: true, menuSection: "Engagement" },
  { path: "/engagement/skills", label: "Skills Board", icon: Psychology, showInMenu: true, menuSection: "Engagement" },

  // ── Profile / Security (hidden from menu) ──
  { path: "/profile", label: "My Profile", icon: PersonPin, showInMenu: false, menuSection: null },
  { path: "/notification-settings", label: "Notification Settings", icon: Campaign, showInMenu: false, menuSection: null },
  { path: "/gate", label: "Security Gate", icon: Shield, showInMenu: false, menuSection: null },

  // ── Help ──
  { path: "/how-to-use", label: "Help & Guide", icon: MenuBook, showInMenu: true, menuSection: "Help" },
];

/* ── Section metadata (icon + color for visual grouping) ── */
export const MENU_SECTIONS = {
  "My Community": { color: "#5c6bc0" },
  Finance: { color: "#43a047" },
  Communication: { color: "#0288d1" },
  Services: { color: "#f57c00" },
  Social: { color: "#1565c0" },
  Engagement: { color: "#f9a825" },
  Help: { color: "#78909c" },
};

/**
 * Get the route config for a given pathname.
 * Handles dynamic routes like /project/:projectNumber
 */
export function getRouteConfig(pathname) {
  const exact = routeConfig.find((r) => r.path === pathname);
  if (exact) return exact;

  const projectMatch = pathname.match(/^\/project\/(.+)$/);
  if (projectMatch) {
    return {
      ...routeConfig.find((r) => r.path === "/project/:projectNumber"),
      dynamicLabel: `Project #${projectMatch[1]}`,
    };
  }

  return null;
}

/**
 * Build breadcrumb trail for a given pathname.
 */
export function getBreadcrumbs(pathname) {
  const current = getRouteConfig(pathname);
  if (!current) return [];

  const crumbs = [];
  let node = current;

  while (node) {
    crumbs.unshift({
      path: node.path === "/project/:projectNumber" ? pathname : node.path,
      label: node.dynamicLabel || node.label,
      icon: node.icon,
    });

    if (node.parent) {
      node = routeConfig.find((r) => r.path === node.parent);
    } else if (node.path !== "/") {
      const home = routeConfig.find((r) => r.path === "/");
      crumbs.unshift({ path: home.path, label: home.label, icon: home.icon });
      break;
    } else {
      break;
    }
  }

  return crumbs;
}

/**
 * Get menu items grouped by section.
 */
export function getMenuItems() {
  return routeConfig.filter((r) => r.showInMenu);
}

/**
 * Check if a menu item should be highlighted as active.
 */
export function isMenuItemActive(menuPath, currentPath) {
  if (menuPath === "/" && currentPath === "/") return true;
  if (menuPath === "/") return false;
  return currentPath === menuPath || currentPath.startsWith(menuPath + "/") || currentPath.startsWith(menuPath + "-");
}

const subRouteParentMap = {
  "/project": "/works",
  "/projects": "/works",
  "/dues": "/works",
  "/review-payments": "/billing-inputs",
  "/invite": "/invitations",
  "/account-form": "/account",
  "/account-details": "/account",
  "/board": "/works",
};

export function getActiveMenuPath(currentPath) {
  const menuItems = getMenuItems();
  if (menuItems.find((m) => m.path === currentPath)) return currentPath;

  for (const [subRoute, parentRoute] of Object.entries(subRouteParentMap)) {
    if (currentPath === subRoute || currentPath.startsWith(subRoute + "/")) {
      return parentRoute;
    }
  }

  return null;
}

export default routeConfig;
